import { toast } from 'sonner';

interface RateLimitEntry {
  attempts: number[];
}

class ClientRateLimiter {
  private limits = new Map<string, RateLimitEntry>();

  canProceed(
    action: string,
    maxAttempts: number = 10,
    windowMs: number = 60000
  ): boolean {
    const now = Date.now();
    const entry = this.limits.get(action) || { attempts: [] };
    
    // Filter out old attempts outside the window
    const recentAttempts = entry.attempts.filter(t => now - t < windowMs);
    
    if (recentAttempts.length >= maxAttempts) {
      const oldestAttempt = Math.min(...recentAttempts);
      const resetIn = Math.ceil((windowMs - (now - oldestAttempt)) / 1000);
      toast.error(`Too many requests. Please try again in ${resetIn} seconds.`);
      return false;
    }
    
    // Record this attempt
    recentAttempts.push(now);
    this.limits.set(action, { attempts: recentAttempts });
    
    return true;
  }

  reset(action: string) {
    this.limits.delete(action);
  }

  getRemainingAttempts(
    action: string,
    maxAttempts: number = 10,
    windowMs: number = 60000
  ): number {
    const now = Date.now();
    const entry = this.limits.get(action);
    if (!entry) return maxAttempts;
    
    const recentAttempts = entry.attempts.filter(t => now - t < windowMs);
    return Math.max(0, maxAttempts - recentAttempts.length);
  }
}

export const clientRateLimiter = new ClientRateLimiter();
